/**
 * Soft Release (SR) and Super Soft Release (SSR): the only way an Under Testing lot moves (user,
 * 2026-09-30; seedtrac QCS p155–159, QCM p217–220). See `lot-holds.ts` for the gate they open.
 */
import { z } from "zod";

export const SOFT_RELEASE_KINDS = ["sr", "ssr"] as const;
export type SoftReleaseKind = (typeof SOFT_RELEASE_KINDS)[number];
export const softReleaseKind = z.enum(SOFT_RELEASE_KINDS);

export const SOFT_RELEASE_KIND_LABELS: Record<SoftReleaseKind, string> = {
  sr: "Soft Release",
  ssr: "Super Soft Release",
};

/** The note each prints: Lot Soft Release Note / Lot Super Soft Release Note. */
export const SOFT_RELEASE_NOTE: Record<SoftReleaseKind, string> = {
  sr: "Lot Soft Release Note (LSRN)",
  ssr: "Lot Super Soft Release Note (LSSRN)",
};

/** Number series: SR/0001/Y/D, SSR/0001/Y/D. */
export const SOFT_RELEASE_SERIES: Record<SoftReleaseKind, string> = {
  sr: "SR",
  ssr: "SSR",
};

/** Who may raise each: SR the QC Supervisor or Manager, SSR the QC Manager only. */
export const SOFT_RELEASE_PERMISSION = {
  sr: "qc.soft_release",
  ssr: "qc.super_soft_release",
} as const;

/** What the lot is released for (seedtrac's SR Type). Recorded and printed; not enforced. */
export const SOFT_RELEASE_PURPOSES = ["condition", "pack", "dispatch"] as const;
export type SoftReleasePurpose = (typeof SOFT_RELEASE_PURPOSES)[number];
export const softReleasePurpose = z.enum(SOFT_RELEASE_PURPOSES);
export const SOFT_RELEASE_PURPOSE_LABELS: Record<SoftReleasePurpose, string> = {
  condition: "Condition",
  pack: "Pack",
  dispatch: "Dispatch",
};

/**
 * The three reasons the dropdown offered (user, 2026-10-24). Since 2026-10-31 the reason is free
 * text and optional; these only label releases saved with one of them.
 */
export const SOFT_RELEASE_REASONS = [
  "processing",
  "packaging",
  "dispatch",
] as const;
export type SoftReleaseReason = (typeof SOFT_RELEASE_REASONS)[number];
export const SOFT_RELEASE_REASON_LABELS: Record<SoftReleaseReason, string> = {
  processing: "Processing",
  packaging: "Packaging",
  dispatch: "Dispatch",
};

/** A stored reason as printed: a dropdown-era value gets its label, free text stays as typed. */
export function softReleaseReasonLabel(reason: string): string {
  return SOFT_RELEASE_REASON_LABELS[reason as SoftReleaseReason] ?? reason;
}

/** The red rule on seedtrac's Add page, in NexSeed's words. */
export const SOFT_RELEASE_RULE: Record<SoftReleaseKind, string> = {
  sr: "Only lots Under Testing / Retest whose GOT is not required are listed.",
  ssr: "Only lots Under Testing / Retest whose GOT is required (GOT-R) are listed.",
};

/**
 * Where a release line stands: on (the lot may move), ended by a pass, failed (the lot was
 * rejected after release: flagged until someone acknowledges it), or cancelled.
 */
export const SOFT_RELEASE_TABS = [
  "active",
  "ended",
  "failed",
  "cancelled",
] as const;
export type SoftReleaseTab = (typeof SOFT_RELEASE_TABS)[number];
export const SOFT_RELEASE_TAB_LABELS: Record<SoftReleaseTab, string> = {
  active: "Active",
  ended: "Passed",
  failed: "Failed after release",
  cancelled: "Cancelled",
};
